import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Ban, ChevronDown, Copy, Sparkles, ShieldCheck, MessageSquareText, Coins } from "lucide-react";
import { toast } from "sonner";
import { Markdown } from "../../../../components/MarkdownLite";
import { cr, toolLabel } from "./sandboxUtil";
import { SCOPES } from "../skillUtil";
import { ComposeCard } from "./ComposeCard";

const card = "aivora-card p-4";
const head = "mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500";

function ArgValue({ v }) {
  const [open, setOpen] = useState(false);
  if (v === null || typeof v !== "object") return <span className="font-mono text-slate-800">{String(v)}</span>;
  return <button type="button" onClick={() => setOpen(!open)} className="text-left font-mono text-[11px] text-brand">{open ? <pre className="whitespace-pre-wrap text-slate-700">{JSON.stringify(v, null, 2)}</pre> : `{…} ${Object.keys(v).length} kunci`}</button>;
}

export function ToolBadge({ tool, testid }) {
  const special = tool === "answer" || tool === "end_skill" || !tool;
  return <span className={`inline-flex items-center rounded-full px-2.5 py-1 font-mono text-[11px] font-bold ${special ? "bg-slate-100 text-slate-700" : "bg-brand text-white"}`} data-testid={testid}>{toolLabel(tool)}</span>;
}

function Decision({ d }) {
  const args = Object.entries(d.args || {});
  return (
    <div className={card} data-testid="sb-decision">
      <p className={head}><Sparkles size={13} className="text-brand" /> Keputusan</p>
      <ToolBadge tool={d.tool} testid="sb-decision-tool" />
      {(d.tool === "answer" || d.tool === "end_skill") && d.raw_tool && d.raw_tool !== d.tool && <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-50 px-2.5 py-2 text-[11px] font-semibold text-amber-800" data-testid="sb-decision-fallback"><AlertTriangle size={13} className="shrink-0" /> Model memilih <code className="font-mono">{d.raw_tool}</code> yang nonaktif/tak dikenal — fallback: jawab langsung.</p>}
      {d.say && <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs italic text-slate-700" data-testid="sb-decision-say">“{d.say}”</p>}
      {args.length > 0 && <table className="mt-2 w-full text-xs" data-testid="sb-decision-args"><tbody>{args.map(([k, v]) => <tr key={k} className="border-t border-line"><td className="py-1.5 pr-3 font-mono font-bold text-slate-500">{k}</td><td className="py-1.5"><ArgValue v={v} /></td></tr>)}</tbody></table>}
    </div>
  );
}

function SkillTools({ r }) {
  const alias = Object.entries(r.aliases || {});
  return (
    <div className={card} data-testid="sb-skill-tools">
      <p className={head}><ShieldCheck size={13} className="text-brand" /> Tool dari skill ini</p>
      <div className="flex flex-wrap gap-1.5">
        {(r.skill_tools || []).map((t) => {
          const off = (r.skill_tools_disabled || []).includes(t); const rec = (r.recommended_tools || []).includes(t);
          const al = alias.find(([, to]) => to === t);
          return <span key={t} title={off ? "Dinonaktifkan di Katalog Tool Asisten" : rec ? "Direkomendasikan ke model" : ""} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[10px] font-bold ${off ? "bg-slate-100 text-slate-400 line-through" : rec ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`} data-testid={`sb-skill-tool-${t}`} data-state={off ? "disabled" : rec ? "recommended" : "plain"}>{off && <Ban size={10} />}{al ? `${al[0]} → ${t}` : t}</span>;
        })}
        {!(r.skill_tools || []).length && <span className="text-[11px] text-slate-400">Skill tidak merujuk tool asisten (tool kartu/aksi seperti `lock_section` tidak dihitung di sini).</span>}
      </div>
    </div>
  );
}

function Validation({ r, scopes, onLocate }) {
  const u = r.unknown_tools || []; const o = r.out_of_scope_tools || [];
  return (
    <div className={card} data-testid="sb-validation">
      <p className={head}><AlertTriangle size={13} className="text-amber-500" /> Validasi teks</p>
      {!u.length && !o.length && <p className="text-[11px] font-semibold text-emerald-600" data-testid="sb-validation-ok">Tidak ada masalah.</p>}
      <div className="flex flex-wrap gap-1.5">
        {u.map((t) => <button key={t} type="button" onClick={() => onLocate(t)} className="rounded-full bg-rose-100 px-2 py-0.5 font-mono text-[10px] font-bold text-rose-700 hover:ring-1 hover:ring-rose-300" title="tidak ada di katalog" data-testid={`sb-unknown-${t}`}>`{t}` · tidak ada di katalog</button>)}
        {o.map((t) => <button key={t} type="button" onClick={() => onLocate(t)} className="rounded-full bg-orange-100 px-2 py-0.5 font-mono text-[10px] font-bold text-orange-700 hover:ring-1 hover:ring-orange-300" data-testid={`sb-oos-${t}`}>`{t}` · tidak tersedia di cakupan {(scopes || []).map((x) => SCOPES[x] || x).join(", ")}</button>)}
      </div>
    </div>
  );
}

function PromptPanel({ r }) {
  const [open, setOpen] = useState(false);
  const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Disalin"));
  return (
    <div className={card} data-testid="sb-prompt">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 text-left text-xs font-bold text-slate-700" data-testid="sb-prompt-toggle"><ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} /> Lihat prompt yang dilihat model</button>
      {open && [["Skill yang disuntikkan", r.rendered_skill, "rendered"], ["Prompt keputusan lengkap", r.system_prompt, "system"]].map(([l, t, k]) => (
        <div key={k} className="mt-3">
          <p className="mb-1 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-slate-400">{l}<button type="button" onClick={() => copy(t || "")} className="inline-flex items-center gap-1 normal-case text-brand" data-testid={`sb-prompt-copy-${k}`}><Copy size={11} /> Salin</button></p>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-xl bg-slate-900 p-3 font-mono text-[11px] leading-relaxed text-slate-100" data-testid={`sb-prompt-${k}`}>{t || "—"}</pre>
        </div>
      ))}
    </div>
  );
}

export function SandboxResult({ r, scopes, onLocate, canAudit, role }) {
  return (
    <div className="space-y-3" data-testid="sb-result">
      <Decision d={r.decision || {}} />
      {r.compose && <ComposeCard c={r.compose} role={["composition", "requirement", "revision"].includes(role) ? role : null} />}
      <SkillTools r={r} />
      <Validation r={r} scopes={r.skill?.scopes || scopes} onLocate={onLocate} />
      {r.answer != null && <div className={card} data-testid="sb-answer"><p className={head}><MessageSquareText size={13} className="text-brand" /> Jawaban dry-run</p><Markdown text={r.answer} className="text-sm" testid="sb-answer-text" /></div>}
      <PromptPanel r={r} />
      <p className="flex items-center justify-between px-1 text-[11px] text-slate-500" data-testid="sb-footer"><span className="inline-flex items-center gap-1"><Coins size={12} /> Kredit terpakai: <b className="font-mono text-slate-800" data-testid="sb-credits">{cr(r.credits)}</b> (akun Anda)</span>{canAudit && <Link to="/audit" className="font-bold text-brand hover:underline" data-testid="sb-audit-link">Audit skills.sandbox →</Link>}</p>
    </div>
  );
}
